import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { EASE } from "./Reveal";

const LINKS = [
  { label: "Home", id: "#home", testid: "nav-link-home" },
  { label: "About", id: "#about", testid: "nav-link-about" },
  { label: "Services", id: "#services", testid: "nav-link-services" },
  { label: "Portfolio", id: "#portfolio", testid: "nav-link-portfolio" },
  { label: "Contact", id: "#contact", testid: "nav-link-contact" },
];

export const Nav = ({ onNavigate }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const isHome = useLocation().pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => onNavigate(id), open ? 400 : 0);
  };

  const dark = scrolled || open;

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.4, ease: EASE }}
        data-testid="main-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-700 ${
          scrolled
            ? "border-b border-line/70 bg-ivory/85 backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 sm:px-10">
          <button
            onClick={() => go("#home")}
            data-testid="nav-logo"
            className={`text-left leading-none transition-colors duration-700 ${
              dark ? "text-ink" : "text-ivory"
            }`}
          >
            {isHome ? (
              <span className="block h-9 w-[86px] sm:h-10 sm:w-[96px]" aria-hidden="true" />
            ) : (
              <img
                src={dark ? "/assets/logo-green.png" : "/assets/logo-ivory.png"}
                alt="SORA events house"
                className="h-9 w-auto transition-opacity duration-700 sm:h-10"
              />
            )}
          </button>

          <nav className="hidden items-center gap-10 lg:flex">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={l.testid}
                onClick={() => go(l.id)}
                className={`group relative text-[11px] font-medium uppercase tracking-[0.3em] transition-colors duration-700 ${
                  dark ? "text-ink" : "text-ivory"
                }`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1.5 left-0 h-px w-0 transition-[width] duration-500 group-hover:w-full ${
                    dark ? "bg-olive" : "bg-pastel"
                  }`}
                />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <button
              data-testid="nav-enquire-button"
              onClick={() => go("#contact")}
              className={`hidden rounded-full border px-7 py-2.5 text-[10px] font-semibold uppercase tracking-[0.3em] transition-all duration-500 sm:inline-flex ${
                dark
                  ? "border-ink/30 text-ink hover:bg-ink hover:text-ivory"
                  : "border-ivory/50 text-ivory hover:bg-ivory hover:text-ink"
              }`}
            >
              Enquire
            </button>
            <button
              data-testid="nav-menu-button"
              aria-label="Toggle menu"
              onClick={() => setOpen(!open)}
              className="flex h-10 w-10 flex-col items-center justify-center gap-[7px] lg:hidden"
            >
              <span
                className={`h-px w-6 transition-all duration-500 ${
                  dark ? "bg-ink" : "bg-ivory"
                } ${open ? "translate-y-[4px] rotate-45" : ""}`}
              />
              <span
                className={`h-px w-6 transition-all duration-500 ${
                  dark ? "bg-ink" : "bg-ivory"
                } ${open ? "-translate-y-[4px] -rotate-45" : ""}`}
              />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ivory px-8 lg:hidden"
          >
            <div className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <span key={l.id} className="block overflow-hidden">
                  <motion.button
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    exit={{ y: "110%" }}
                    transition={{ duration: 0.8, delay: 0.08 * i, ease: EASE }}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    onClick={() => go(l.id)}
                    className="block py-2 text-left font-serif text-5xl font-light text-ink"
                  >
                    {l.label}
                  </motion.button>
                </span>
              ))}
            </div>
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.5 }}
              data-testid="mobile-nav-enquire-button"
              onClick={() => go("#contact")}
              className="mt-12 inline-flex w-fit rounded-full border border-ink/30 px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-ink"
            >
              Enquire
            </motion.button>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.6 }}
              className="absolute bottom-10 left-8 text-[10px] uppercase tracking-[0.35em] text-olive"
            >
              Dubai, UAE
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
